import { AnimatedSkeletonList } from "@/components/common/AnimatedSkeletonList/AnimatedSkeletonList"
import { BreadcrumbsNavigation } from "@/components/common/BreadcrumbsNavigation/BreadcrumbsNavigation"
import { Button } from "@/components/common/Button/Button"
import { AutoFillIcon } from "@/components/common/icons/AutoFillIcon/AutoFillIcon"
import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { ActionSidebarRight } from "@/components/feature/ActionSidebarRight/ActionSidebarRight"
import { CollapsibleQuestionnaireList } from "@/components/feature/CollapsibleQuestionnaireList/CollapsibleQuestionnaireList"
import { QuestionItem } from "@/components/feature/CollapsibleQuestionnaireList/QuestionItem/QuestionItem"
import { ContinueLaterModal } from "@/components/feature/Modals/ContinueLaterModal/ContinueLaterModal"
import { DeleteQuestionnaireModal } from "@/components/feature/Modals/DeleteQuestionnaireModal/DeleteQuestionnaireModal"
import { QuestionnaireExtractionErrorCard } from "@/components/feature/QuestionnaireExtractionErrorCard/QuestionnaireExtractionErrorCard"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { PERMISSIONS } from "@/constants/permissions"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import {
  ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  useOnboardingQuestionnaireData,
} from "@/features/onboarding"
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs/useBreadcrumbs"
import { useQuestionnaireStatusNavigation } from "@/hooks/useQuestionnaireStatusNavigation/useQuestionnaireStatusNavigation"
import { useTextAreaStates } from "@/hooks/useTextAreaStates/useTextAreaStates"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import {
  useAutofillQuestionnaire,
  useEditResponsePair,
} from "@/services/api/questionnaireService"
import { EditResponsePairKey } from "@/types/hooks/useTextAreaStates"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { isExcelFileName } from "@/utils/fileUtils"
import { isLimitError } from "@/utils/limitErrors"
import { getRouteByStatus } from "@/utils/questionnaire/questionnaireStatus"
import { reduceNumTotalQuestions } from "@/utils/questionnaire/reduceQuestions"
import { statusErrorCodeTranslationMap } from "@/utils/questionnaire/statusErrorTranslation"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"

const EDIT_RESPONSE_PAIR_KEY: EditResponsePairKey = "question"

export const QuestionsPage: React.FC = () => {
  const { translate } = useTranslation()
  const featureFlags = useGetAllFeatureFlags()
  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  const {
    data: questionnaire,
    isPending: isQuestionnairePending,
    isFetched: isQuestionnaireFetched,
    isError: isQuestionnaireError,
    isDemo: isDemoQuestionnaire,
  } = useOnboardingQuestionnaireData(questionnaireId)

  const [, setQuestionnaireStatus] = useQuestionnaireStatusNavigation(
    questionnaire,
    {
      enableAutoRedirect: false,
    },
  )
  const { breadcrumbs } = useBreadcrumbs()

  const navigate = useNavigate()

  const [numTotalQuestions, setNumTotalQuestions] = useState(0)

  const [isContinueLaterModalOpen, setIsContinueLaterModalOpen] =
    useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const isFailed = questionnaire?.status === QuestionnaireStatus.Failed

  const failureErrorCode = questionnaire?.errorCode

  const failureErrorMessage = failureErrorCode
    ? translate(statusErrorCodeTranslationMap[failureErrorCode])
    : questionnaire?.errorMessage || ""

  useEffect(() => {
    if (isQuestionnaireFetched && questionnaire) {
      if (
        questionnaire.status === QuestionnaireStatus.New ||
        questionnaire.status === QuestionnaireStatus.Extracting
      ) {
        navigate(`/questionnaire/${questionnaire.id}/processing`, {
          replace: true,
        })
        return
      }

      setQuestionnaireStatus(questionnaire.status)
      setNumTotalQuestions(reduceNumTotalQuestions(questionnaire))
    }
  }, [
    isQuestionnaireFetched,
    questionnaire,
    navigate,
    isQuestionnaireError,
    setQuestionnaireStatus,
    translate,
  ])

  const handleOpenContinueLaterModal = () => {
    setIsContinueLaterModalOpen(true)
  }

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true)
  }

  const {
    data: autofillTaskGroupTrigger,
    mutate: triggerAutofillAnswers,
    isPending: isAutofillTriggerPending,
    isSuccess: isAutofillTriggerSuccess,
    isError: isAutofillTriggerError,
    error: autofillTriggerError,
  } = useAutofillQuestionnaire()

  const handleTriggerAutofill = () => {
    if (
      isDemoQuestionnaire ||
      questionnaireId === ONBOARDING_DEMO_QUESTIONNAIRE_ID
    ) {
      toast.warning(translate("ONBOARDING/DEMO_ACTION_DISABLED"))
      return
    }

    if (questionnaire && questionnaireId) {
      trackEvent(AnalyticsEvent.AUTO_FILL_STARTED, {
        questionnaire_id: questionnaireId,
        question_count: numTotalQuestions,
      })
      triggerAutofillAnswers(questionnaireId)
    }
  }

  const handlePreviewQuestions = () => {
    const isExcel = isExcelFileName(questionnaire?.fileName || "")
    if (isExcel) {
      navigate(`/questionnaire/${questionnaireId}/qm-questions`, {
        replace: true,
      })
    } else {
      navigate(`/questionnaire/${questionnaireId}/pdf-questions`, {
        replace: true,
      })
    }
  }

  useEffect(() => {
    if (isAutofillTriggerError) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(autofillTriggerError)) {
        toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
      }
      trackEvent(AnalyticsEvent.AUTO_FILL_FAILED, {
        questionnaire_id: questionnaireId,
        errorMessage: "Autofill trigger failed",
      })
    } else if (isAutofillTriggerSuccess && autofillTaskGroupTrigger) {
      navigate(
        getRouteByStatus(
          autofillTaskGroupTrigger.questionnaireId,
          QuestionnaireStatus.GeneratingAnswers,
          questionnaire?.fileName,
          questionnaire?.hasPositionData,
          autofillTaskGroupTrigger.id,
          featureFlags,
        ),
      )
    }
  }, [
    isAutofillTriggerSuccess,
    autofillTaskGroupTrigger,
    questionnaire?.fileName,
    navigate,
    isAutofillTriggerError,
    autofillTriggerError,
    translate,
    questionnaire?.hasPositionData,
    questionnaireId,
    featureFlags,
  ])

  const { mutate: mutateResponsePair } = useEditResponsePair()
  const [{ textsCount }] = useTextAreaStates(
    questionnaire,
    mutateResponsePair,
    EDIT_RESPONSE_PAIR_KEY,
  )

  if (!questionnaireId) {
    toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR"))
    return null
  }

  let contentCenter = <></>
  let contentRight: React.ReactNode = undefined

  // Show processing view if questionnaire is still being extracted
  if (isFailed) {
    contentCenter = (
      <QuestionnaireExtractionErrorCard message={failureErrorMessage} />
    )
  } else if (isQuestionnairePending && !isAutofillTriggerPending) {
    contentCenter = (
      <>
        <Typography as="h1" variant="titleL">
          {translate("QUESTIONNAIRE_QUESTIONS/TITLE_VIEWING_QUESTIONS")}
        </Typography>
        <AnimatedSkeletonList />
      </>
    )
  } else if (isQuestionnaireFetched && !autofillTaskGroupTrigger) {
    contentCenter = (
      <div>
        <div
          className={classNames("flex items-center justify-between", "pb-L")}
        >
          <Typography as="h1" variant="titleL">
            {translate("QUESTIONNAIRE_QUESTIONS/TITLE_VIEWING_QUESTIONS")}
          </Typography>
          <Typography as="p" variant="textM">
            {translate("QUESTIONNAIRE_QUESTIONS/COUNT_TOTAL_QUESTIONS")}{" "}
            <Typography
              as="span"
              variant="textM"
              className="text-yellow1"
              dataTestId={"questionnaire-total"}
            >
              {numTotalQuestions}
            </Typography>
          </Typography>
        </div>
        {questionnaire?.sections.map((section, index) => {
          return (
            <CollapsibleQuestionnaireList
              key={`${section.id}-${index}`}
              dataTestId="questionnaire-section"
              sectionIndex={index}
              sectionTitle={section.name}
              sectionCountDisplay={
                <Typography as="p" variant="textM">
                  {translate("QUESTIONNAIRE_QUESTIONS/COUNT_SECTION_QUESTIONS")}{" "}
                  <Typography
                    as="span"
                    variant="textM"
                    className="text-yellow1"
                  >
                    {section.responsePairs.length}
                  </Typography>
                </Typography>
              }
            >
              {section.responsePairs.map((pair, index) => {
                return (
                  <QuestionItem
                    key={`${pair.id}-${index}`}
                    questionnaireId={questionnaireId}
                    responsePair={pair}
                  />
                )
              })}
            </CollapsibleQuestionnaireList>
          )
        })}
      </div>
    )

    contentRight = questionnaire && (
      <ActionSidebarRight
        questionnaire={questionnaire}
        handleOpenDeleteModal={handleOpenDeleteModal}
        actionButtons={
          <>
            <PermissionGate permission={PERMISSIONS.QUEST_RUN}>
              <Button
                variantColor="yellow"
                title={translate("QUESTIONNAIRE_QUESTIONS/GENERATE_ANSWERS")}
                label={translate("QUESTIONNAIRE_QUESTIONS/GENERATE_ANSWERS")}
                onClick={() => handleTriggerAutofill()}
                variantSize="medium"
                isFullWidth={true}
                IconRight={AutoFillIcon}
                isDisabled={
                  isQuestionnairePending ||
                  isAutofillTriggerPending ||
                  numTotalQuestions !== textsCount.total ||
                  isDemoQuestionnaire
                }
              />
            </PermissionGate>

            {isExcelFileName(questionnaire.fileName) &&
            questionnaire.hasPositionData &&
            featureFlags.KB_TAGGING_FLOW ? (
              <Button
                variantColor="dark"
                title={translate("QUESTIONNAIRE/PREVIEW_QUESTIONS")}
                label={translate("QUESTIONNAIRE/PREVIEW_QUESTIONS")}
                onClick={handlePreviewQuestions}
                variantSize="medium"
                isFullWidth={true}
                isDisabled={isQuestionnairePending || isAutofillTriggerPending}
                className="border-primary text-primary hover:text-white"
              />
            ) : null}

            <Button
              variantColor="dark"
              title={translate("QUESTIONNAIRE/CONTINUE_LATER")}
              label={translate("QUESTIONNAIRE/CONTINUE_LATER")}
              onClick={handleOpenContinueLaterModal}
              variantSize="medium"
              isFullWidth={true}
              isDisabled={isQuestionnairePending || isAutofillTriggerPending}
            />
          </>
        }
      />
    )
  } else if (isQuestionnaireError) {
    contentCenter = (
      <Typography as="h2" variant="titleM" className="text-red1">
        {translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR")}
      </Typography>
    )
  }

  return (
    <MainLayout
      headerLeftElement={<BreadcrumbsNavigation breadcrumbs={breadcrumbs} />}
      contentCenter={
        <>
          {isContinueLaterModalOpen && (
            <ContinueLaterModal
              setIsContinueLaterModalOpen={setIsContinueLaterModalOpen}
            />
          )}
          {isDeleteModalOpen && (
            <DeleteQuestionnaireModal
              questionnaireId={questionnaireId}
              setIsDeleteModalOpen={setIsDeleteModalOpen}
            />
          )}
          {contentCenter}
        </>
      }
      contentRight={contentRight}
    ></MainLayout>
  )
}
